import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  DatePicker,
  Row,
  Col,
  InputNumber,
  Drawer,
  Divider,
  Tooltip,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  EyeOutlined,
  MailOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const VAT_RATE = 13;

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const STATUS_COLORS = {
  draft: 'default',
  sent: 'blue',
  accepted: 'green',
  rejected: 'red',
  expired: 'orange',
  cancelled: 'default',
};

const Quotations = () => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isBuildPreviewVisible, setIsBuildPreviewVisible] = useState(false);
  const [isItemsModalVisible, setIsItemsModalVisible] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [form] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [lineItems, setLineItems] = useState([]);
  const [billingType, setBillingType] = useState('standard');
  const vatRate = Form.useWatch('vat_rate', form) || VAT_RATE;
  const formValues = Form.useWatch([], form) || {};
  const queryClient = useQueryClient();

  const {
    data: quotationsData,
    isLoading,
    refetch,
  } = useQuery(
    ['quotations', searchText, statusFilter],
    async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');
      const response = await api.get(`/admin/quotations?${params.toString()}`);
      return response.data.data;
    },
    { keepPreviousData: true },
  );

  const { data: productsData } = useQuery(
    ['products-services', 'quotations'],
    async () => {
      const response = await api.get('/admin/products-services?per_page=1000');
      return response.data?.data?.data ?? response.data?.data ?? response.data ?? [];
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const catalogue = Array.isArray(productsData) ? productsData : [];

  const createQuotation = useMutation((payload) => api.post('/admin/quotations', payload), {
    onSuccess: () => {
      message.success('Quotation created successfully.');
      queryClient.invalidateQueries(['quotations']);
      closeDrawer();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create quotation.');
    },
  });

  const updateQuotation = useMutation(
    ({ id, payload }) => api.put(`/admin/quotations/${id}`, payload),
    {
      onSuccess: () => {
        message.success('Quotation updated successfully.');
        queryClient.invalidateQueries(['quotations']);
        closeDrawer();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update quotation.');
      },
    },
  );

  const deleteQuotation = useMutation((id) => api.delete(`/admin/quotations/${id}`), {
    onSuccess: () => {
      message.success('Quotation deleted.');
      queryClient.invalidateQueries(['quotations']);
    },
    onError: () => message.error('Failed to delete quotation.'),
  });

  const sendEmail = useMutation(
    ({ id, email }) => api.post(`/admin/quotations/${id}/send`, { email }),
    {
      onSuccess: () => {
        message.success('Quotation sent by email.');
        setIsEmailModalVisible(false);
        setEmailTarget(null);
        emailForm.resetFields();
      },
      onError: (error) => message.error(error.response?.data?.message || 'Failed to send email.'),
    },
  );

  const totalItems = lineItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0),
    0,
  );
  const vatAmount = billingType === 'vat' ? totalItems * (vatRate / 100) : 0;
  const grandTotal = totalItems + vatAmount;

  const pickItem = (id) => {
    const product = catalogue.find((p) => p.id === Number(id));
    if (!product) return;
    const existing = lineItems.find((li) => li.product_service_id === Number(id));
    if (existing) {
      setLineItems(
        lineItems.map((li) =>
          li.product_service_id === Number(id) ? { ...li, quantity: li.quantity + 1 } : li,
        ),
      );
      return;
    }
    setLineItems([
      ...lineItems,
      {
        key: Date.now(),
        product_service_id: product.id,
        name: product.name,
        type: product.type || 'product',
        unit: product.unit || 'pcs',
        quantity: 1,
        unit_price: product.sale_price ?? product.price ?? 0,
      },
    ]);
  };

  const updateLine = (index, field, value) => {
    setLineItems(lineItems.map((li, i) => (i === index ? { ...li, [field]: value } : li)));
  };

  const removeLine = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const emptyLine = () => ({
    key: Date.now(),
    product_service_id: null,
    name: '',
    type: 'product',
    unit: 'pcs',
    quantity: 1,
    unit_price: 0,
  });

  const openCreate = () => {
    setEditingQuotation(null);
    setLineItems([]);
    setBillingType('standard');
    form.resetFields();
    form.setFieldsValue({
      billing_type: 'standard',
      quote_date: dayjs(),
      valid_until: dayjs().add(7, 'day'),
    });
    setIsDrawerVisible(true);
  };

  const openEdit = async (quotation) => {
    setEditingQuotation(quotation);
    setBillingType(quotation.billing_type || 'standard');
    setLineItems([]);
    form.resetFields();
    form.setFieldsValue({
      customer_name: quotation.customer_name,
      customer_email: quotation.customer_email,
      customer_phone: quotation.customer_phone,
      customer_address: quotation.customer_address,
      quote_date: quotation.quote_date ? dayjs(quotation.quote_date) : null,
      valid_until: quotation.valid_until ? dayjs(quotation.valid_until) : null,
      billing_type: quotation.billing_type,
      vat_rate: quotation.vat_rate || VAT_RATE,
      status: quotation.status,
      notes: quotation.notes,
    });
    setIsDrawerVisible(true);

    try {
      const response = await api.get(`/admin/quotations/${quotation.id}`);
      const full = response.data?.data ?? quotation;
      setEditingQuotation(full);
      setLineItems(
        (full.items || []).map((item) => ({
          key: item.id,
          product_service_id: item.product_service_id,
          name: item.name,
          type: item.type,
          unit: item.unit,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
        })),
      );
    } catch {
      message.error('Failed to load quotation details.');
    }
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setIsItemsModalVisible(false);
    setEditingQuotation(null);
    setLineItems([]);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (lineItems.length === 0) {
        message.warning('Please add at least one item.');
        return;
      }
      const payload = {
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone,
        customer_address: values.customer_address,
        quote_date: values.quote_date ? values.quote_date.format('YYYY-MM-DD') : undefined,
        valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : undefined,
        billing_type: values.billing_type,
        vat_rate: values.vat_rate || VAT_RATE,
        status: values.status || 'draft',
        notes: values.notes,
        items: lineItems.map((li) => ({
          product_service_id: li.product_service_id || null,
          name: li.name,
          type: li.type,
          unit: li.unit,
          quantity: Number(li.quantity || 0),
          unit_price: Number(li.unit_price || 0),
        })),
      };
      if (editingQuotation) {
        updateQuotation.mutate({ id: editingQuotation.id, payload });
      } else {
        createQuotation.mutate(payload);
      }
    } catch {
      /* validation errors already shown */
    }
  };

  const downloadPdf = async (quotation) => {
    try {
      const response = await api.get(`/admin/quotations/${quotation.id}/pdf/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Quotation-${quotation.quotation_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      message.error('Failed to download PDF.');
    }
  };

  const previewPdf = async (quotation) => {
    try {
      const response = await api.get(`/admin/quotations/${quotation.id}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
      setIsPreviewVisible(true);
    } catch {
      message.error('Failed to preview PDF.');
    }
  };

  const openEmailModal = (quotation) => {
    setEmailTarget(quotation);
    emailForm.setFieldsValue({
      email: quotation.customer_email || '',
    });
  };

  const sendEmailHandler = async () => {
    try {
      const values = await emailForm.validateFields();
      sendEmail.mutate({ id: emailTarget.id, email: values.email });
    } catch {
      /* validation errors already shown */
    }
  };

  const confirmDelete = (quotation) => {
    Modal.confirm({
      title: 'Delete Quotation',
      content: `Are you sure you want to delete quotation ${quotation.quotation_number}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => deleteQuotation.mutate(quotation.id),
    });
  };

  const columns = [
    {
      title: 'Quotation No.',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      render: (value, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openEdit(record)}>
          <strong>{value}</strong>
        </Button>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.customer_email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.customer_email}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'quote_date',
      key: 'quote_date',
      render: (value) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
    },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      render: (value) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
    },
    {
      title: 'Items',
      key: 'items_count',
      dataIndex: 'items_count',
      width: 70,
      align: 'center',
    },
    {
      title: 'Type',
      dataIndex: 'billing_type',
      key: 'billing_type',
      width: 90,
      render: (value) => (value === 'vat' ? <Tag color="blue">VAT</Tag> : <Tag>Standard</Tag>),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value) => (
        <strong>
          Rs. {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value) => (
        <Tag color={STATUS_COLORS[value] || 'default'}>
          {String(value || 'draft').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 210,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Preview PDF">
            <Button size="small" icon={<EyeOutlined />} onClick={() => previewPdf(record)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadPdf(record)} />
          </Tooltip>
          <Tooltip title="Send Email">
            <Button size="small" icon={<MailOutlined />} onClick={() => openEmailModal(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const lineColumns = [
    {
      title: 'Item',
      key: 'name',
      render: (_, record, index) => (
        <Space>
          <Select
            showSearch
            style={{ width: 220 }}
            placeholder="Search product or service"
            value={record.product_service_id || undefined}
            optionFilterProp="label"
            onChange={(value) => {
              const product = catalogue.find((p) => p.id === Number(value));
              if (product) {
                updateLine(index, 'product_service_id', product.id);
                updateLine(index, 'name', product.name);
                updateLine(index, 'type', product.type || 'product');
                updateLine(index, 'unit', product.unit || 'pcs');
                updateLine(index, 'unit_price', product.sale_price ?? product.price ?? 0);
              }
            }}
            options={[
              ...(record.product_service_id &&
              !catalogue.some((p) => p.id === Number(record.product_service_id))
                ? [
                    {
                      value: record.product_service_id,
                      label: `${record.name} (${record.type === 'service' ? 'Service' : 'Product'})`,
                    },
                  ]
                : []),
              ...catalogue.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.type === 'service' ? 'Service' : 'Product'})`,
              })),
            ]}
          />
          <Input
            style={{ width: 220 }}
            value={record.name}
            onChange={(e) => updateLine(index, 'name', e.target.value)}
          />
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (value) =>
        value === 'service' ? <Tag color="purple">Service</Tag> : <Tag color="blue">Product</Tag>,
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      render: (value, record, index) => (
        <Input value={value} onChange={(e) => updateLine(index, 'unit', e.target.value)} />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          style={{ width: '100%' }}
          value={value}
          onChange={(v) => updateLine(index, 'quantity', v)}
        />
      ),
    },
    {
      title: 'Unit Price (Rs.)',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 130,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={value}
          onChange={(v) => updateLine(index, 'unit_price', v)}
        />
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (_, record) => (
        <strong>
          Rs.{' '}
          {((record.quantity || 0) * (record.unit_price || 0)).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </strong>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 50,
      render: (_, __, index) => (
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeLine(index)} />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ marginBottom: 0 }}>
              <FileTextOutlined /> Quotations
            </Title>
            <Text type="secondary">Create, share, and print product/service quotations.</Text>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Quotation
            </Button>
          </Col>
        </Row>

        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder="Search by number, customer, email"
            style={{ width: 280 }}
            onSearch={setSearchText}
          />
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: 160 }}
            onChange={setStatusFilter}
          >
            <Option value="draft">Draft</Option>
            <Option value="sent">Sent</Option>
            <Option value="accepted">Accepted</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="expired">Expired</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
          <Button icon={<FileTextOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={Array.isArray(quotationsData?.data) ? quotationsData.data : []}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <Drawer
        title={
          editingQuotation ? `Edit Quotation ${editingQuotation.quotation_number}` : 'New Quotation'
        }
        width={820}
        open={isDrawerVisible}
        onClose={closeDrawer}
        destroyOnClose
        extra={
          <Space>
            <Button icon={<EyeOutlined />} onClick={() => setIsBuildPreviewVisible(true)}>
              Preview
            </Button>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={createQuotation.isLoading || updateQuotation.isLoading}
              onClick={handleSubmit}
            >
              {editingQuotation ? 'Update Quotation' : 'Save Quotation'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Card size="small" title="Customer Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer_name"
                  label="Customer Name"
                  rules={[{ required: true, message: 'Customer name is required' }]}
                >
                  <Input placeholder="Walk-in Customer" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customer_email" label="Email">
                  <Input type="email" placeholder="customer@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customer_phone" label="Phone">
                  <Input placeholder="98XXXXXXXX" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customer_address" label="Address">
                  <Input placeholder="Kathmandu, Nepal" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Quotation Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="quote_date"
                  label="Quote Date"
                  rules={[{ required: true, message: 'Date is required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="valid_until" label="Valid Until">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="billing_type" label="Billing Type" rules={[{ required: true }]}>
                  <Select onChange={setBillingType}>
                    <Option value="standard">Standard</Option>
                    <Option value="vat">VAT ({VAT_RATE}%)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vat_rate" label="VAT Rate (%)" initialValue={VAT_RATE}>
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="Status" initialValue="draft">
                  <Select>
                    <Option value="draft">Draft</Option>
                    <Option value="sent">Sent</Option>
                    <Option value="accepted">Accepted</Option>
                    <Option value="rejected">Rejected</Option>
                    <Option value="expired">Expired</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="small"
            title={
              <>Items {lineItems.length > 0 && <Tag color="blue">{lineItems.length} added</Tag>}</>
            }
            extra={
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsItemsModalVisible(true)}
              >
                Manage Items
              </Button>
            }
          >
            {lineItems.length === 0 ? (
              <Empty
                description="No items added yet. Click Manage Items to add products/services."
                style={{ padding: '16px 0' }}
              />
            ) : (
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                columns={[
                  { title: 'Item', key: 'name', dataIndex: 'name' },
                  {
                    title: 'Type',
                    key: 'type',
                    width: 90,
                    render: (_, r) =>
                      r.type === 'service' ? (
                        <Tag color="purple">Service</Tag>
                      ) : (
                        <Tag color="blue">Product</Tag>
                      ),
                  },
                  {
                    title: 'Qty',
                    key: 'qty',
                    width: 60,
                    align: 'center',
                    render: (_, r) => r.quantity,
                  },
                  { title: 'Unit', key: 'unit', dataIndex: 'unit', width: 70 },
                  {
                    title: 'Unit Price',
                    key: 'unit_price',
                    width: 110,
                    align: 'right',
                    render: (_, r) => `Rs. ${fmt(r.unit_price)}`,
                  },
                  {
                    title: 'Amount',
                    key: 'amount',
                    width: 120,
                    align: 'right',
                    render: (_, r) => `Rs. ${fmt((r.quantity || 0) * (r.unit_price || 0))}`,
                  },
                ]}
                dataSource={lineItems}
              />
            )}
            <Divider />
            <Row justify="space-between">
              <Col>
                <Space direction="vertical" size={2}>
                  <Text type="secondary">Subtotal: Rs. {fmt(totalItems)}</Text>
                  {billingType === 'vat' && vatAmount > 0 && (
                    <Text type="secondary">
                      VAT ({vatRate}%): Rs. {fmt(vatAmount)}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col>
                <Text strong style={{ fontSize: 18 }}>
                  Grand Total: Rs. {fmt(grandTotal)}
                </Text>
              </Col>
            </Row>
          </Card>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Terms, delivery timeline, etc." />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title="Manage Quotation Items"
        open={isItemsModalVisible}
        onCancel={() => setIsItemsModalVisible(false)}
        width={1200}
        footer={
          <Space>
            <Button onClick={() => setIsItemsModalVisible(false)}>Close</Button>
            <Button type="primary" onClick={() => setIsItemsModalVisible(false)}>
              Done
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            showSearch
            style={{ width: 360 }}
            placeholder="Add product / service"
            optionFilterProp="label"
            value={null}
            onChange={pickItem}
            options={catalogue.map((p) => ({
              value: p.id,
              label: `${p.name} — Rs. ${fmt(p.sale_price ?? p.price ?? 0)} (${p.type === 'service' ? 'Service' : 'Product'})`,
            }))}
          />
          <Button icon={<PlusOutlined />} onClick={() => setLineItems([...lineItems, emptyLine()])}>
            Add Blank Line
          </Button>
          <Text type="secondary">
            {lineItems.length} item{lineItems.length === 1 ? '' : 's'}
          </Text>
        </Space>
        {lineItems.length === 0 ? (
          <Empty
            description="No items yet. Use the search above or add a blank line."
            style={{ padding: '32px 0' }}
          />
        ) : (
          <Table
            rowKey="key"
            columns={lineColumns}
            dataSource={lineItems}
            pagination={false}
            size="middle"
            scroll={{ x: 1020 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4} align="right">
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>Rs. {fmt(totalItems)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  {billingType === 'vat' && vatAmount > 0 && (
                    <Text type="secondary">
                      VAT {vatRate}%: Rs. {fmt(vatAmount)}
                    </Text>
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            )}
          />
        )}
        <Divider />
        <Row justify="end">
          <Col>
            <Text strong style={{ fontSize: 18 }}>
              Grand Total: Rs. {fmt(grandTotal)}
            </Text>
          </Col>
        </Row>
      </Modal>

      <Modal
        title="Send Quotation by Email"
        open={isEmailModalVisible}
        onOk={sendEmailHandler}
        onCancel={() => setIsEmailModalVisible(false)}
        confirmLoading={sendEmail.isLoading}
        footer={null}
      >
        {emailTarget && (
          <Text type="secondary">
            Attach PDF of quotation <strong>{emailTarget.quotation_number}</strong> to:
          </Text>
        )}
        <Form form={emailForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="email"
            label="Recipient Email"
            rules={[{ required: true, type: 'email', message: 'Valid email required' }]}
          >
            <Input placeholder="customer@example.com" />
          </Form.Item>
          <Row justify="end">
            <Space>
              <Button onClick={() => setIsEmailModalVisible(false)}>Cancel</Button>
              <Button type="primary" loading={sendEmail.isLoading} onClick={sendEmailHandler}>
                Send
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Quotation Preview"
        open={isPreviewVisible}
        footer={null}
        width="90%"
        onCancel={() => {
          setIsPreviewVisible(false);
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        }}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            title="Quotation PDF Preview"
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        )}
      </Modal>

      <Modal
        title={
          editingQuotation
            ? `Preview Quotation ${editingQuotation.quotation_number}`
            : 'Preview New Quotation'
        }
        open={isBuildPreviewVisible}
        onCancel={() => setIsBuildPreviewVisible(false)}
        footer={null}
        width={900}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
          <div
            style={{
              borderBottom: '3px solid #2c3e50',
              paddingBottom: 12,
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <div>
              <div style={{ fontSize: 22, fontWeight: 'bold', color: '#2c3e50' }}>Current Care</div>
              <Text type="secondary">Quotation</Text>
            </div>
            <Text strong>{editingQuotation?.quotation_number || 'Draft'}</Text>
          </div>

          <Row gutter={24}>
            <Col span={14}>
              <Text strong>Customer</Text>
              <div style={{ marginTop: 4 }}>
                <div>
                  <strong>{formValues.customer_name || '—'}</strong>
                </div>
                {formValues.customer_email && (
                  <div>
                    <Text type="secondary">Email:</Text> {formValues.customer_email}
                  </div>
                )}
                {formValues.customer_phone && (
                  <div>
                    <Text type="secondary">Phone:</Text> {formValues.customer_phone}
                  </div>
                )}
                {formValues.customer_address && (
                  <div>
                    <Text type="secondary">Address:</Text> {formValues.customer_address}
                  </div>
                )}
              </div>
            </Col>
            <Col span={10} style={{ textAlign: 'right' }}>
              <div>
                <Text type="secondary">Date: </Text>
                {formValues.quote_date ? dayjs(formValues.quote_date).format('MMM D, YYYY') : '—'}
              </div>
              <div>
                <Text type="secondary">Valid Until: </Text>
                {formValues.valid_until ? dayjs(formValues.valid_until).format('MMM D, YYYY') : '—'}
              </div>
              <div>
                <Text type="secondary">Billing: </Text>
                {formValues.billing_type === 'vat' ? `VAT (${vatRate}%)` : 'Standard'}
              </div>
              <div>
                <Text type="secondary">Status: </Text>
                {formValues.status ? String(formValues.status).toUpperCase() : '—'}
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0 12px' }} />

          {lineItems.length === 0 ? (
            <Empty description="No items added yet" style={{ padding: '24px 0' }} />
          ) : (
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              columns={[
                {
                  title: '#',
                  key: 'idx',
                  width: 36,
                  render: (_, __, index) => index + 1,
                },
                { title: 'Item', key: 'name', dataIndex: 'name' },
                {
                  title: 'Type',
                  key: 'type',
                  width: 90,
                  render: (_, record) => (record.type === 'service' ? 'Service' : 'Product'),
                },
                {
                  title: 'Qty',
                  key: 'qty',
                  width: 60,
                  align: 'center',
                  render: (_, record) => record.quantity,
                },
                { title: 'Unit', key: 'unit', width: 70, dataIndex: 'unit' },
                {
                  title: 'Unit Price',
                  key: 'price',
                  width: 120,
                  align: 'right',
                  render: (_, record) => `Rs. ${fmt(record.unit_price)}`,
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  width: 120,
                  align: 'right',
                  render: (_, record) =>
                    `Rs. ${fmt((record.quantity || 0) * (record.unit_price || 0))}`,
                },
              ]}
              dataSource={lineItems}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5} align="right">
                    <strong>Subtotal</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={2} align="right">
                    <strong>Rs. {fmt(totalItems)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          )}

          {billingType === 'vat' && vatAmount > 0 && (
            <Row justify="end" style={{ marginTop: 12, marginBottom: 4 }}>
              <Col>
                <Text type="secondary">
                  VAT ({vatRate}%): Rs. {fmt(vatAmount)}
                </Text>
              </Col>
            </Row>
          )}

          <Row justify="end" style={{ marginTop: 8 }}>
            <Col>
              <Text strong style={{ fontSize: 18 }}>
                Grand Total: Rs. {fmt(grandTotal)}
              </Text>
            </Col>
          </Row>

          {formValues.notes && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Notes:</Text> <Text type="secondary">{formValues.notes}</Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Quotations;
